import React, { useState } from 'react';
import { Loader2, MessageCircle } from 'lucide-react';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { ConversationSelector } from './ConversationSelector';
import { SendTemplateModal } from '@/components/whatsapp/SendTemplateModal';
import { useConversation, type ConversationChannel, type Message } from '../hooks/useConversation';
import { cn } from '@/lib/utils';
import { useCanAccessCustomerService } from '@/hooks/useCanAccessCustomerService';

function buildQuotedBody(reply: Message, newText: string): string {
  const raw = (reply.body || '').replace(/^>\s?.+?(?:\n\n|\n)/, '').trim();
  const quoted = raw
    ? raw.slice(0, 200)
    : reply.media_mimetype?.startsWith('image/') ? '📷 Imagem'
    : reply.media_mimetype?.startsWith('audio/') ? '🎤 Mensagem de voz'
    : reply.media_mimetype?.startsWith('video/') ? '🎬 Vídeo'
    : 'Anexo';
  return `> ${quoted}\n\n${newText}`;
}

export interface ChatCoreProps {
  contactId: string;
  contactPhone?: string;
  leadId?: string;
  leadPhone?: string;
  header?: React.ReactNode;
  initialChannel?: ConversationChannel;
  showConversationSelector?: boolean;
  className?: string;
  onClose?: () => void;
}

export function ChatCore({
  contactId,
  contactPhone,
  leadId,
  leadPhone,
  header,
  initialChannel = 'twilio',
  showConversationSelector = false,
  className,
}: ChatCoreProps) {
  const [newMessage, setNewMessage] = useState('');
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const { canAccess: canAccessCustomerService } = useCanAccessCustomerService();

  const conversation = useConversation({
    contactId,
    leadId,
    contactPhone,
    leadPhone,
    initialChannel: canAccessCustomerService ? initialChannel : 'twilio',
  });

  const phoneNumber = conversation.phoneNumber;

  // Loading state
  if (conversation.isLoading) {
    return (
      <div className={cn('flex flex-col items-center justify-center h-full bg-card', className)}>
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // No phone
  if (!phoneNumber) {
    return (
      <div className={cn('flex flex-col items-center justify-center h-full bg-card text-center p-4', className)}>
        <MessageCircle className="w-12 h-12 text-muted-foreground mb-2" />
        <p className="text-muted-foreground font-medium">Sem número de telefone</p>
        <p className="text-sm text-muted-foreground">
          Adicione um número para enviar mensagens no WhatsApp
        </p>
      </div>
    );
  }

  const handleSend = () => {
    if (!newMessage.trim() && !conversation.selectedFile) return;
    const text = replyingTo ? buildQuotedBody(replyingTo, newMessage) : newMessage;
    conversation.sendMessage(text);
    setNewMessage('');
    setReplyingTo(null);
  };

  return (
    <div className={cn('flex flex-col h-full min-h-[400px] bg-card rounded-lg border', className)}>
      {/* Header */}
      {header}

      {/* Channel selector */}
      {showConversationSelector && canAccessCustomerService && (
        <ConversationSelector
          value={conversation.selectedChannel}
          onChange={conversation.setSelectedChannel}
        />
      )}

      {/* Error */}
      {conversation.error && (
        <div className="px-4 py-2 bg-destructive/10 text-destructive text-sm flex-shrink-0">
          <p>{conversation.error}</p>
        </div>
      )}

      {/* Messages */}
      <MessageList
        messages={conversation.filteredMessages}
        messagesEndRef={conversation.messagesEndRef}
        onReply={setReplyingTo}
      />

      {/* Input */}
      <MessageInput
        value={newMessage}
        onChange={setNewMessage}
        onSend={handleSend}
        onSendAudio={conversation.sendAudio}
        onFileSelect={conversation.handleFileSelect}
        onOpenTemplates={() => setTemplateModalOpen(true)}
        
        selectedFile={conversation.selectedFile}
        isUploading={conversation.isUploading}
        onClearFile={conversation.clearSelectedFile}
        isSending={conversation.isSending}
        replyingTo={replyingTo}
        onCancelReply={() => setReplyingTo(null)}
      />

      <SendTemplateModal
        open={templateModalOpen}
        onOpenChange={setTemplateModalOpen}
        phoneNumber={phoneNumber}
        contactId={contactId}
        leadId={leadId}
        onTemplateSent={() => conversation.reloadMessages()}
      />
    </div>
  );
}
